import { Extension } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { ReactRenderer } from '@tiptap/react'
import Suggestion from '@tiptap/suggestion'
import tippy from 'tippy.js'

import { deriveRunbookRefAlias } from './derive-alias'
import {
  RunbookRefMentionMenuList,
  type RunbookRefMentionItem,
} from './runbook-ref-mention-menu-list'

const extensionName = 'runbookRefMentionSuggestion'

interface RunbookRefMentionSuggestionStorage {
  // Per-instance so a sibling editor's onDestroy can't null the
  // popup a freshly-mounted editor just created.
  popup: any | null
}

export interface RunbookRefMentionSuggestionOptions {
  /**
   * Returns the refs available at the current cursor position. Called
   * synchronously each time the user types after `@`.
   */
  getItems: (query: string) => RunbookRefMentionItem[]
  /**
   * Optional: when set, the menu appends a "Create parameter" item
   * whenever the typed query has no matching alias. Selecting it calls
   * this callback with the trimmed name and inserts `@params.<name>`
   * into the document.
   */
  onCreateParam?: (name: string) => void
}

/**
 * RFC-167 §5.8.1: `@`-trigger suggestion that scopes to RunbookParams +
 * step outputs + run/env bindings available at the cursor. Inserts a
 * `runbookRefMention` atom on selection.
 */
export const RunbookRefMentionSuggestion = Extension.create<
  RunbookRefMentionSuggestionOptions,
  RunbookRefMentionSuggestionStorage
>({
  name: extensionName,

  priority: 200,

  addOptions() {
    return {
      getItems: () => [],
    }
  },

  addStorage() {
    return { popup: null }
  },

  onCreate() {
    this.storage.popup = tippy('body', {
      interactive: true,
      trigger: 'manual',
      placement: 'bottom-start',
      maxWidth: '20rem',
      offset: [0, 8],
      popperOptions: {
        strategy: 'fixed',
        modifiers: [{ name: 'flip', enabled: false }],
      },
    })
  },

  onDestroy() {
    this.storage.popup?.[0]?.destroy()
    this.storage.popup = null
  },

  addProseMirrorPlugins() {
    // Capture for closures — plugin callbacks fire after this method
    // returns and must read live storage, not closure snapshots.
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- TipTap callbacks need the extension instance after this method returns.
    const ext = this
    return [
      Suggestion({
        editor: this.editor,
        char: '@',
        allowSpaces: false,
        startOfLine: false,
        allowedPrefixes: null,
        pluginKey: new PluginKey(extensionName),
        items: ({ query }) => {
          const baseItems = ext.options.getItems(query)
          const trimmedQuery = query.trim()
          const lower = trimmedQuery.toLowerCase()

          const filtered = !trimmedQuery
            ? baseItems.slice(0, 30)
            : baseItems
                .filter((item) => {
                  const alias = item.alias || deriveRunbookRefAlias(item.ref)
                  return (
                    alias.toLowerCase().includes(lower) ||
                    item.ref.toLowerCase().includes(lower) ||
                    item.label.toLowerCase().includes(lower) ||
                    item.description?.toLowerCase().includes(lower) ||
                    item.disambiguator?.toLowerCase().includes(lower)
                  )
                })
                .slice(0, 30)

          // Always surface a "Create parameter" footer when the host
          // wired up an `onCreateParam` handler — authors discover the
          // pattern even before typing a name. We only suppress it when
          // the typed query exactly matches an existing alias (it'd be
          // redundant with the matching ref already visible above).
          const exactMatch =
            trimmedQuery &&
            baseItems.some(
              (item) =>
                (item.alias || deriveRunbookRefAlias(item.ref)).toLowerCase() ===
                lower
            )

          if (ext.options.onCreateParam && !exactMatch) {
            filtered.push({
              ref: trimmedQuery
                ? `@params.${trimmedQuery}`
                : '@params.__new__',
              alias: trimmedQuery,
              label: trimmedQuery
                ? `Create parameter ${trimmedQuery}`
                : 'Create parameter…',
              description: trimmedQuery
                ? 'Define a new procedure parameter'
                : 'Type a name to create a new procedure parameter',
              scope: 'params',
              kind: 'create',
            })
          }

          return filtered
        },
        command: ({ editor, range, props }: any) => {
          const item = props as RunbookRefMentionItem
          if (item.kind === 'create') {
            const name = (item.alias || '').trim()
            if (!name) {
              // No name typed yet — drop the `@` trigger and let the
              // author keep typing rather than inserting an empty pill.
              editor.chain().focus().deleteRange(range).run()
              return
            }
            ext.options.onCreateParam?.(name)
          }
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertRunbookRefMention({ ref: item.ref })
            .insertContent(' ')
            .run()
        },
        render: () => {
          let component: ReactRenderer | null = null

          return {
            onStart: (props: any) => {
              component = new ReactRenderer(RunbookRefMentionMenuList, {
                props,
                editor: props.editor,
              })
              if (!props.clientRect) return
              ext.storage.popup?.[0]?.setProps({
                getReferenceClientRect: props.clientRect,
                appendTo: () => document.body,
                content: component.element,
              })
              ext.storage.popup?.[0]?.show()
            },
            onUpdate: (props: any) => {
              component?.updateProps(props)
              if (!props.clientRect) return
              ext.storage.popup?.[0]?.setProps({
                getReferenceClientRect: props.clientRect,
              })
            },
            onKeyDown: (props: any) => {
              if (props.event.key === 'Escape') {
                ext.storage.popup?.[0]?.hide()
                return true
              }
              return (component?.ref as any)?.onKeyDown?.(props) ?? false
            },
            onExit: () => {
              ext.storage.popup?.[0]?.hide()
              component?.destroy()
            },
          }
        },
      }),
    ]
  },
})
